<template>
  <div class="customer-picker">
    <select v-model="selectedCommunityId" class="ui dropdown" @change="handleCommunityChange">
      <option value="" disabled selected hidden>View Destination ({{ filteredCustomers.length }})</option>
      <option v-for="customer in filteredCustomers" :key="`super-${customer.customerId}`" :value="customer.customerId">
        <template v-if="customer.subType === 'Original'"> {{ customer.customerId }}: {{ customer.title }}</template>
        <template v-else>{{ customer.title }} ({{ customer.subType.toLowerCase() }})</template>
        <template v-if="customer.isTest"> - Test</template>
        <template v-if="customer.isDisabled"> - Disabled</template>
      </option>
    </select>
    <input v-model="searchQuery" type="text" class="ui input" placeholder="filter" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import axios from "axios";
import { useUserProfileStore } from "@stores/userProfileStore";

interface CustomerIdDetailsPayload {
  customerId: string;
  title: string;
  state: string;
  isDisabled: boolean;
  isTest: boolean;
  subType: string;
  createdDate: string;
}

const emit = defineEmits<{
  (e: "customerSelected", communityId: string): void;
}>();

const userProfileStore = useUserProfileStore();
const isSuperOrSalesUser = computed(() => userProfileStore.isSuperOrSalesUser);

const customers = ref<CustomerIdDetailsPayload[]>([]);
const selectedCommunityId = ref<string | null>("");
const searchQuery = ref("");

const filteredCustomers = computed(() => {
  if (!searchQuery.value) return customers.value;

  const query = searchQuery.value.toLowerCase();
  return customers.value.filter(
    customer =>
      customer.customerId.toLowerCase().includes(query) ||
      customer.title.toLowerCase().includes(query) ||
      customer.subType.toLowerCase().includes(query) ||
      (customer.isDisabled && "disabled".includes(query))
  );
});

const getAllCommunityIds = async () => {
  const response = await axios.get(`${import.meta.env.VITE_API_URL}/pages/ids-with-detail?includeTestCustomers=true`);
  customers.value = response.data.sort((a, b) => {
    const fieldA = a.subType === "Original" ? a.customerId : a.title;
    const fieldB = b.subType === "Original" ? b.customerId : b.title;
    return fieldA.localeCompare(fieldB);
  });
};

const handleCommunityChange = () => {
  if (selectedCommunityId.value && selectedCommunityId.value.length > 0) {
    emit("customerSelected", selectedCommunityId.value);
  }
};

onMounted(async () => {
  if (isSuperOrSalesUser.value) {
    await getAllCommunityIds();
  }
});
</script>

<style scoped>
.customer-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 350px;
}

.ui.input {
  width: 100%;
}
</style>
